import { useEffect, useContext, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { Modal, ModalTitle, MODAL_OUTER_PADDING } from 'design-system/src/components/Modal';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage';
import { client } from '../../api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AuthContext } from '../../auth';
import { useChiliPiper, CHILI_PIPER_LOAD_ERROR_MESSAGE } from '../../utils/chiliPiper';

export type ScheduleTrainingModalProps = {
  onClose: () => void;
  open: boolean;
};

const CHILI_PIPER_CONTAINER_ID = 'chili-piper-training-calendar';

const CustomModalTitle = styled(ModalTitle)`
  padding: ${MODAL_OUTER_PADDING};
`;

const CustomModalBody = styled.div`
  box-sizing: border-box;
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background-color: #f7f8fa;
`;

// Container for Chili Piper inline embed
// position: relative is required per Chili Piper docs
// Let the iframe fill the modal body and handle its own scrolling
const ChiliPiperContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;

  /* Style the iframe that Chili Piper injects */
  iframe {
    border: none;
    width: 100% !important;
    height: 100% !important;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

/**
 * A modal with a page for scheduling training using Chili Piper.
 * NOTE: This component does not have a storybook story. This is because the Chili Piper calendar
 * would change between renders within the iframe because it shows the time. To include the story we
 * would have to ignore the whole iframe, which would make the story useless.
 */
const ScheduleTrainingModal = ({ open, onClose }: ScheduleTrainingModalProps) => {
  const { user } = useContext(AuthContext);
  const queryClient = useQueryClient();

  const recordTrainingBooking = useMutation({
    mutationFn: async () => {
      await client.post('/onboarding/report-training-session-action', {
        action: 'booked',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sidebarModalProps'] });
      queryClient.invalidateQueries({ queryKey: ['onboardingInfo'] });
    },
  });

  const leadData = useMemo(() => ({ PersonEmail: user?.email ?? '' }), [user?.email]);

  const handleSuccess = useCallback(() => {
    recordTrainingBooking.mutate();
  }, [recordTrainingBooking]);

  const { isLoading, scriptError, reset } = useChiliPiper({
    enabled: open,
    containerId: CHILI_PIPER_CONTAINER_ID,
    router: 'training-router',
    trigger: 'InAppButton',
    leadData,
    onSuccess: handleSuccess,
  });

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  return (
    <Modal wide fullHeight onClose={onClose} open={open}>
      <CustomModalTitle>Schedule training</CustomModalTitle>
      <CustomModalBody>
        {scriptError ? (
          <LoadingContainer>
            <ErrorMessage textStyledAs="bodySmallDMSans">{CHILI_PIPER_LOAD_ERROR_MESSAGE}</ErrorMessage>
          </LoadingContainer>
        ) : (
          <ChiliPiperContainer id={CHILI_PIPER_CONTAINER_ID}>
            {isLoading && (
              <LoadingContainer>
                <LoadingSpinner />
              </LoadingContainer>
            )}
          </ChiliPiperContainer>
        )}
      </CustomModalBody>
    </Modal>
  );
};

export default ScheduleTrainingModal;
